import { get } from "lodash";
import { useState } from "react";

// Utility function to check if a string contains HTML
function isHTML(str: string): boolean {
  if (!str || typeof str !== "string") return false;
  const doc = new DOMParser().parseFromString(str, "text/html");
  return Array.from(doc.body.childNodes).some(node => node.nodeType === 1);
}

// Utility function to copy content with formatting preserved
async function copyFormattedContent(content: string): Promise<void> {
  if (!content || typeof content !== "string") {
    await navigator.clipboard.writeText(content || "");
    return;
  }

  if (!isHTML(content)) {
    // Plain text - just copy as text
    await navigator.clipboard.writeText(content);
    return;
  }

  // HTML content - copy both HTML and plain text versions
  const doc = new DOMParser().parseFromString(content, "text/html");
  const htmlContent = doc.body.innerHTML;
  const textContent = doc.body.textContent || doc.body.innerText || content;

  try {
    await navigator.clipboard.write([
      new ClipboardItem({
        "text/html": new Blob([htmlContent], { type: "text/html" }),
        "text/plain": new Blob([textContent], { type: "text/plain" }),
      }),
    ]);
  } catch (error) {
    // Fallback to plain text if clipboard API fails
    await navigator.clipboard.writeText(textContent);
  }
}

export function CopyField({ field }: { field: { value?: any } }): JSX.Element {
  const [isCopied, setIsCopied] = useState(false);
  const fieldValue = get(field, "value");

  const handleCopy = async () => {
    try {
      await copyFormattedContent(fieldValue);
      setIsCopied(true);

      // Reset the copied state after 2 seconds
      setTimeout(() => {
        setIsCopied(false);
      }, 2000);
    } catch (error) {
      console.error("Failed to copy content:", error);
    }
  };

  return (
    <button
      data-cy="copy-field"
      type="button"
      className="in-blue-500"
      onClick={handleCopy}
    >
      <i className={`icon icon--16 icon-copy font-13 mr-8`} />
      <span> {isCopied ? "Copied!" : "Copy"} </span>
    </button>
  );
}
